/**
 * Event list (`/me/events`).
 *
 * Two independent lists from one response: what the visitor organizes and what
 * they joined. The create button is the documented invite gate — it is always
 * rendered, and only its target changes when the account is not invited yet.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { EventSummaryDto } from '@shared/types'

import { EventRowCard } from '../../components/event/event-cards'
import { PageHeader } from '../../components/layout/page-header'
import { ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/primitives'
import { useCurrentUser } from '../../hooks/use-auth'
import { useEventList } from '../../hooks/use-events'

interface EventSectionProps {
  title: string
  events: EventSummaryDto[]
  /** Link target per row; a joined event opens its share page. */
  to: (event: EventSummaryDto) => string
}

function EventSection({ title, events, to }: EventSectionProps) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {events.length === 0 ? (
        <EmptyState title={t('events.empty')} description={t('common.empty')} />
      ) : (
        <div className="grid gap-3">
          {events.map((event) => (
            <EventRowCard key={event.id} event={event} to={to(event)} />
          ))}
        </div>
      )}
    </section>
  )
}

export function EventsPage() {
  const { t } = useTranslation()
  const { isInvited, isAdmin } = useCurrentUser()
  const eventList = useEventList()

  // The gate: invited accounts and administrators go straight to the wizard — the
  // server lets both create — and everyone else is sent to redeem a code first.
  const createTarget = isInvited || isAdmin ? '/me/events/new/title' : '/me/invite'

  return (
    <div className="container-page flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.events') },
        ]}
        title={t('events.title')}
        actions={
          <Button asChild>
            <Link to={createTarget}>{t('events.createButton')}</Link>
          </Button>
        }
      />

      {eventList.isPending ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : null}

      {eventList.isError ? (
        <ErrorBlock
          title={t('error.internal_error')}
          retryLabel={t('common.retry')}
          onRetry={() => void eventList.refetch()}
        />
      ) : null}

      {eventList.data ? (
        <>
          <EventSection
            title={t('events.createdTitle')}
            events={eventList.data.created}
            to={(event) => `/me/events/${event.id}`}
          />
          <EventSection
            title={t('events.joinedTitle')}
            events={eventList.data.joined}
            to={(event) => `/e/${event.shareToken}`}
          />
        </>
      ) : null}
    </div>
  )
}
